import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { WorkOrdersSquare } from 'types/squares.type';
import type { Asset } from 'types/asset.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

type AssetToYValueMap = Record<string, number>;

export const WorkOrderTimeline = ({
  data,
  enableDarkTheme,
  assetList,
  selectedAsset,
  assetOrder,
}: {
  data: WorkOrdersSquare[];
  enableDarkTheme: boolean;
  assetList: Asset[];
  selectedComponent: string;
  selectedAsset: string;
  assetOrder: string[] | null;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const allowedIds = useMemo(
    () => assetList.map((item) => item.bumperNumber),
    [assetList]
  );

  const filteredData = useMemo(() => {
    let dataToPlot = data.filter((item) => allowedIds.includes(item.asset_id));
    if (selectedAsset !== 'All') {
      dataToPlot = dataToPlot.filter((item) => item.asset_id === selectedAsset);
    }
    return dataToPlot;
  }, [data, allowedIds, selectedAsset]);

  const chartData = filteredData
    .map((item) => ({
      ...item,
      dateTimestamp: new Date(item.date_of_orders).getTime(),
      dateFormatted: item.date_of_orders,
    }))
    .sort((a, b) => b.dateTimestamp - a.dateTimestamp);

  const uniqueAssets = [...new Set(filteredData.map((item) => item.asset_id))];

  const sortedUniqueAssets = useMemo(() => {
    if (assetOrder?.length) {
      return assetOrder
        .filter((assetId) => uniqueAssets.includes(assetId))
        .reverse();
    }
    return uniqueAssets.reverse();
  }, [uniqueAssets, assetOrder]);

  const assetToYValue = useMemo<AssetToYValueMap>(() => {
    const result: AssetToYValueMap = {};

    sortedUniqueAssets.forEach((assetId, index) => {
      result[assetId] = index;
    });

    return result;
  }, [sortedUniqueAssets]);

  const finalChartData = useMemo(
    () =>
      chartData.map((item) => ({
        ...item,
        yValue: assetToYValue[item.asset_id] ?? 0,
      })),
    [chartData, assetToYValue]
  );

  const fontColor = palette.textPrimary;

  return (
    <Box
      sx={{
        border: '1px solid #2F445E',
        padding: '10px',
        borderRadius: '4px',
        marginBottom: '10px',
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: '600',
          color: fontColor,
          mb: 2,
        }}
      >
        {t('work_order_timeline')}
      </Typography>
      {finalChartData.length === 0 ? (
        <Typography
          sx={{
            color: fontColor,
            fontSize: '14px',
          }}
        >
          {t('no_timeline_data_for_selected_component_and_time_range')}
        </Typography>
      ) : (
        <div style={{ height: uniqueAssets.length * 60 + 100 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 10, bottom: 20, left: 25 }}>
              <CartesianGrid
                strokeDasharray="2 2"
                stroke={enableDarkTheme ? '#263238' : '#E0E0E0'}
                vertical={false}
              />
              <XAxis
                type="number"
                dataKey="dateTimestamp"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(timestamp) => {
                  const date = new Date(timestamp);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                }}
                tick={{
                  fontSize: 14,
                  fill: palette.textSecondary,
                }}
                label={{
                  value: 'Date of Orders',
                  position: 'bottom',
                  fill: palette.textSecondary,
                  fontSize: 14,
                }}
              />
              <YAxis
                type="number"
                dataKey="yValue"
                domain={[-0.5, sortedUniqueAssets.length - 0.5]}
                ticks={sortedUniqueAssets.map((_, index) => index)}
                tickFormatter={(value) => sortedUniqueAssets[value]}
                tick={{
                  fontSize: 14,
                  fill: palette.textSecondary,
                }}
                label={{
                  value: 'Asset ID',
                  angle: -90,
                  position: 'insideLeft',
                  dy: 16,
                  dx: -25,
                  fill: palette.textSecondary,
                  fontSize: 14,
                }}
              />
              <Tooltip content={<CustomTooltip />} cursor={false} />
              <Scatter
                name="Work Orders"
                data={finalChartData}
                fill={palette.primary}
                shape="circle"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </Box>
  );
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: any;
}) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div
        style={{
          backgroundColor: 'rgba(217, 229, 247, 0.8)',
          border: '1px solid #ccc',
          color: '#000',
          padding: '3px 5px',
          borderRadius: '4px',
          fontSize: '10px',
        }}
      >
        <p className="font-semibold">{data.asset_id}</p>
        <p className="text-sm text-gray-600">
          {t('date')} {data.date_of_orders}
        </p>
        <p className="text-sm text-gray-600">
          {t('wo')} {data.work_order_number}
        </p>
        <p className="text-sm text-gray-600">
          {t('status')} {data.work_order_status}
        </p>
        <p className="text-sm text-gray-600">
          {t('component')} {data.components_category}
        </p>
        <p className="text-sm text-gray-600">
          {t('subcomponent')}{' '}
          {data.sub_component_categories.replace(/^\[|\]$/g, '')}
        </p>
        <p className="text-sm text-gray-600">
          {t('action')} {data.actions_performed}
        </p>
      </div>
    );
  }
  return null;
};
